import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { APP_INITIALIZER, NgModule } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { LoggingInterceptor } from '@app/core/interceptors/logging.interceptor';
import { PythonApiHeadersInterceptor } from '@app/core/interceptors/python-api-headers-interceptor';
import { AppCommonModule } from '@common/app-common.module';
import { InvoiceUploaderModule } from '@modules/invoice-uploader/invoice-uploader.module';
import { NavigationModule } from '@modules/navigation/navigation.module';
import { NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { AuthModule, StsConfigLoader } from 'angular-auth-oidc-client';
import { LoggerModule, NgxLoggerLevel } from 'ngx-logger';
import { firstValueFrom } from 'rxjs';

import { environment } from '../environments/environment';

import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import * as coreInterceptors from './core/interceptors';
import * as messageBoxSharedComponents from './shared/message-box/components';
import * as toastSharedComponents from './shared/toast/components';
import * as toastSharedServices from './shared/toast/services';

import { AppSettingsService } from './core/app-settings/service/app-settings.service';
import { logFinalEffectiveConfiguration } from './core/configuration/configuration-diagnostics';
import { PlatformStsConfigurationLoader } from './core/configuration/platform-sts-configuration.loader';
import { AppSettingsStore } from './core/state/app-settings-store/app-settings-store';

import { AuthService } from '@modules/auth/services';

export function initializeApp(
    platformConfigurationLoader: PlatformStsConfigurationLoader,
    authService: AuthService,
    appSettingsService: AppSettingsService
) {
    return async () => {
        // Resolve and validate the public OIDC configuration first. Keeping this
        // in the same initializer preserves strict ordering through callback
        // processing and the authenticated tenant-settings request.
        await platformConfigurationLoader.initialize();
        await authService.initializeUser();
        if (await authService.isLoggedIn()) {
            await firstValueFrom(appSettingsService.loadAppSettings());
        } else {
            logFinalEffectiveConfiguration(
                platformConfigurationLoader.source,
                platformConfigurationLoader.effectiveConfiguration,
                platformConfigurationLoader.publicConfiguration,
                null
            );
        }
    };
}

@NgModule({
    declarations: [
        AppComponent,
        ...messageBoxSharedComponents.components,
        toastSharedComponents.components,
    ],
    bootstrap: [AppComponent],
    imports: [
        BrowserModule,
        ReactiveFormsModule,
        AppRoutingModule,
        BrowserAnimationsModule,
        NgbModule,
        AppCommonModule.forRoot(),
        InvoiceUploaderModule,
        NavigationModule.forRoot(),
        LoggerModule.forRoot({
            level: environment.loggerConfig.level, // Client-side level
            // Keep actionable warnings and errors visible in the browser console.
            // The environment level still suppresses lower-priority messages.
            disableConsoleLogging: false,
            serverLogLevel: NgxLoggerLevel.OFF, // Server logging is handled via HttpClient in LoggingService
            serverLoggingUrl: environment.loggerConfig.serverLoggingUrl, // Points to your Serilog (or other) logging API
        }),
        AuthModule.forRoot({
            loader: {
                provide: StsConfigLoader,
                useExisting: PlatformStsConfigurationLoader,
            },
        }),
    ],
    providers: [
        // AuthGuard is registered via `providedIn: 'root'` and composed in app-routing.module.ts.
        toastSharedServices.services,
        ...coreInterceptors.interceptors,
        { provide: HTTP_INTERCEPTORS, useClass: PythonApiHeadersInterceptor, multi: true },
        { provide: HTTP_INTERCEPTORS, useClass: LoggingInterceptor, multi: true },
        provideHttpClient(withInterceptorsFromDi()),
        AppSettingsService,
        AppSettingsStore,
        {
            provide: APP_INITIALIZER,
            useFactory: initializeApp,
            deps: [PlatformStsConfigurationLoader, AuthService, AppSettingsService],
            multi: true,
        },
    ],
    exports: [toastSharedComponents.components],
})
export class AppModule {}
